/**
 * Self-Registration Service
 *
 * Provides public (no-auth) API calls for patient self-registration.
 * Uses a separate axios instance without auth interceptors to avoid
 * redirect-to-login behavior on unauthenticated endpoints.
 */
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

// Public API client — no auth token, no 401 redirect
const publicClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Types ───────────────────────────────────────────────────────────

export interface SelfRegistrationConfig {
  enabled: boolean;
  programCode: string | null;
  programName: string;
  programUrl: string | null;
  formId: string | null;
  formVersion: number | null;
  formSchema: unknown | null;
  consentRequired: boolean;
  consentText: string | null;
  requireDuplicateCheck: boolean;
  duplicateCheckFields: string[];
  enableHospitalSelection: boolean;
  enableHcpSelection: boolean;
  restrictHcpsBySelectedHospital: boolean;
  requireHcpSelection: boolean;
  autoRouteApprovalToSelectedHcp: boolean;
}

export interface SelfRegistrationHospitalOption {
  hospitalId: string;
  hospitalCode: string;
  hospitalName: string;
}

export interface SelfRegistrationHcpOption {
  hcpId: string;
  hospitalId?: string;
  hcpCode: string;
  hcpName: string;
  hospitalName?: string;
  approvalEnabled: boolean;
}

export interface SelfRegistrationValidationResult {
  hospitals: number;
  hcps: number;
  approvableHcps: number;
  issues: string[];
}

export interface SelfRegistrationSubmission {
  programCode: string;
  formData: Record<string, unknown>;
  consentAccepted: boolean;
  hospitalId?: string;
  hcpId?: string;
  website?: string; // Honeypot field — must remain empty for legitimate submissions
}

export interface SelfRegistrationResult {
  patientId: string;
  patientCode: string;
  message: string;
  workflowState: string;
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  matchedPatientIds: string[];
  matchedFields: string[];
  message: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  errors: string[];
  correlationId: string;
}

// ─── API Functions ───────────────────────────────────────────────────

/**
 * Fetch self-registration configuration for a program (public, no auth).
 */
export async function getSelfRegistrationConfig(
  programCode: string
): Promise<SelfRegistrationConfig> {
  const response = await publicClient.get<ApiResponse<SelfRegistrationConfig>>(
    `/public/programs/${encodeURIComponent(programCode)}/self-registration-config`
  );
  return response.data?.data ?? {
    enabled: false,
    programCode: null,
    programName: '',
    programUrl: null,
    formId: null,
    formVersion: null,
    formSchema: null,
    consentRequired: false,
    consentText: null,
    requireDuplicateCheck: false,
    duplicateCheckFields: [],
    enableHospitalSelection: true,
    enableHcpSelection: true,
    restrictHcpsBySelectedHospital: true,
    requireHcpSelection: true,
    autoRouteApprovalToSelectedHcp: true,
  };
}

/**
 * Fetch self-registration configuration by program URL (public, no auth).
 * Useful for root login pages where route does not carry a program code.
 */
export async function getSelfRegistrationConfigByUrl(
  programUrl: string
): Promise<SelfRegistrationConfig> {
  const response = await publicClient.get<ApiResponse<SelfRegistrationConfig>>(
    '/public/programs/self-registration-config-by-url',
    { params: { programUrl } }
  );

  return response.data?.data ?? {
    enabled: false,
    programCode: null,
    programName: '',
    programUrl: null,
    formId: null,
    formVersion: null,
    formSchema: null,
    consentRequired: false,
    consentText: null,
    requireDuplicateCheck: false,
    duplicateCheckFields: [],
    enableHospitalSelection: true,
    enableHcpSelection: true,
    restrictHcpsBySelectedHospital: true,
    requireHcpSelection: true,
    autoRouteApprovalToSelectedHcp: true,
  };
}

export async function getSelfRegistrationHospitals(
  programCode: string
): Promise<SelfRegistrationHospitalOption[]> {
  const response = await publicClient.get<ApiResponse<SelfRegistrationHospitalOption[]>>(
    `/public/programs/${encodeURIComponent(programCode)}/self-registration-hospitals`
  );

  return response.data?.data ?? [];
}

export async function getSelfRegistrationHcps(
  programCode: string,
  params?: { hospitalId?: string; search?: string }
): Promise<SelfRegistrationHcpOption[]> {
  const response = await publicClient.get<ApiResponse<SelfRegistrationHcpOption[]>>(
    `/public/programs/${encodeURIComponent(programCode)}/self-registration-hcps`,
    {
      params: {
        hospitalId: params?.hospitalId,
        search: params?.search,
      },
    }
  );

  return response.data?.data ?? [];
}

/**
 * Validate whether self-registration routing prerequisites are satisfied.
 */
export async function getSelfRegistrationValidation(
  programCode: string
): Promise<SelfRegistrationValidationResult> {
  const response = await publicClient.get<ApiResponse<SelfRegistrationValidationResult>>(
    `/public/programs/${encodeURIComponent(programCode)}/self-registration/validation`
  );

  return response.data?.data ?? {
    hospitals: 0,
    hcps: 0,
    approvableHcps: 0,
    issues: [],
  };
}

/**
 * Submit patient self-registration (public, no auth).
 */
export async function submitSelfRegistration(
  programCode: string,
  data: SelfRegistrationSubmission
): Promise<SelfRegistrationResult> {
  const response = await publicClient.post<ApiResponse<SelfRegistrationResult>>(
    `/public/programs/${encodeURIComponent(programCode)}/self-register`,
    data
  );

  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Registration failed');
  }

  return response.data.data;
}

/**
 * Check for duplicate patients before submission (public, no auth).
 */
export async function checkDuplicatePatient(
  programCode: string,
  formData: Record<string, unknown>
): Promise<DuplicateCheckResult> {
  const response = await publicClient.post<ApiResponse<DuplicateCheckResult>>(
    `/public/programs/${encodeURIComponent(programCode)}/check-duplicate`,
    formData
  );
  return response.data?.data ?? { isDuplicate: false, matchedPatientIds: [], matchedFields: [], message: '' };
}

// ─── Public Entity Lookup (for Entity Selector fields in self-registration) ──

export interface PublicEntityLookupResult {
  entityId: string;
  entityCode: string;
  displayText: string;
  entityType: string;
  parentEntityId?: string;
  status?: string;
}

export interface PublicEntityLookupPagedResult {
  items: PublicEntityLookupResult[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PublicEntityHierarchy {
  programId: string;
  programName?: string;
  hospitalId?: string;
  hospitalName?: string;
  hospitalCode?: string;
  hcpId?: string;
  hcpName?: string;
  hcpCode?: string;
}

export interface PublicEntityLookupParams {
  search?: string;
  parentEntityId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Public entity search for self-registration entity selector fields.
 * Supports HOSPITAL and HCP only.
 */
export async function publicEntityLookupSearch(
  programCode: string,
  entityType: string,
  params: PublicEntityLookupParams = {}
): Promise<PublicEntityLookupPagedResult> {
  const response = await publicClient.get<ApiResponse<PublicEntityLookupPagedResult>>(
    `/public/programs/${encodeURIComponent(programCode)}/entity-lookup/${encodeURIComponent(entityType)}`,
    { params }
  );
  return response.data?.data ?? { items: [], totalCount: 0, page: 1, pageSize: 20 };
}

/**
 * Public entity get by ID for resolving selected entity display.
 */
export async function publicEntityLookupGetById(
  programCode: string,
  entityType: string,
  entityId: string
): Promise<PublicEntityLookupResult | null> {
  const response = await publicClient.get<ApiResponse<PublicEntityLookupResult>>(
    `/public/programs/${encodeURIComponent(programCode)}/entity-lookup/${encodeURIComponent(entityType)}/${entityId}`
  );
  return response.data?.data ?? null;
}

/**
 * Public hierarchy resolution for auto-mapping in self-registration.
 */
export async function publicEntityLookupHierarchy(
  programCode: string,
  entityType: string,
  entityId: string
): Promise<PublicEntityHierarchy | null> {
  const response = await publicClient.get<ApiResponse<PublicEntityHierarchy>>(
    `/public/programs/${encodeURIComponent(programCode)}/entity-lookup/${encodeURIComponent(entityType)}/${entityId}/hierarchy`
  );
  return response.data?.data ?? null;
}
